import { Loader, Text } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import ContactOptionBody from 'src/components/Inputs/CarrierAutocomplete/ContactOptionBody'
import {
  formatContactCount,
  type KnownContact,
} from 'src/components/Inputs/CarrierAutocomplete/knownContacts'
import {
  PickList,
  usePickListKeyboard,
} from 'src/components/Inputs/PickList/PickList'
import {
  FormPhoneField,
  FormTextField,
} from 'src/components/Overlays/EmploymentDetailsDrawer/components/FormComponents'

import type { EmploymentForm } from './formValues'
import { SectionLink, SectionTitle, StageActions } from './stageParts'

const CONTACTS_ID = 'add-employment-known-contacts'

export interface ContactStageProps {
  /** The contact is settled and the job stage is showing; render it read-only. */
  collapsed: boolean
  form: EmploymentForm
  contacts: KnownContact[]
  loading: boolean
  /** The list of contacts we hold is offered (until a pick or "a different contact"). */
  showList: boolean
  onPickContact: (contact: KnownContact, rank: number) => void
  onDifferentContact: () => void
  onShowList: () => void
  onContinue: () => void
  onChange: () => void
  /** The section is folded to its title line. */
  folded?: boolean
  onToggleFold?: () => void
}

const ContactRows = ({
  form,
  editing,
}: {
  form: EmploymentForm
  editing: boolean
}) => (
  <>
    <FormTextField
      label="Contact Name"
      editing={editing}
      formProps={{
        ...form.getInputProps('contactName'),
        placeholder: 'Enter contact name',
      }}
    />
    <FormTextField
      label="Email"
      editing={editing}
      formProps={{
        ...form.getInputProps('email'),
        placeholder: 'Enter email address',
      }}
    />
    <FormPhoneField
      label="Phone"
      editing={editing}
      formProps={{ ...form.getInputProps('phone') }}
    />
  </>
)

const ContactStage = ({
  collapsed,
  form,
  contacts,
  loading,
  showList,
  onPickContact,
  onDifferentContact,
  onShowList,
  onContinue,
  onChange,
  folded = false,
  onToggleFold,
}: ContactStageProps) => {
  const trailing = {
    caption: 'None of these',
    label: 'A different contact',
    onPick: onDifferentContact,
  }
  const keyboard = usePickListKeyboard({
    items: contacts,
    trailing,
    onPick: (contact, index) => onPickContact(contact, index + 1),
  })

  if (collapsed) {
    return (
      <div className="space-y-3">
        <SectionTitle
          folded={folded}
          onToggle={onToggleFold}
          action={<SectionLink onClick={onChange}>Change</SectionLink>}
        >
          Contact information
        </SectionTitle>
        {!folded && <ContactRows form={form} editing={false} />}
      </div>
    )
  }

  const listOffered = showList && (loading || contacts.length > 0)

  return (
    <div className="space-y-3">
      <SectionTitle folded={folded} onToggle={onToggleFold}>
        Contact information
      </SectionTitle>

      {folded ? null : listOffered ? (
        loading ? (
          <div className="flex justify-center py-2">
            <Loader size={18} color="gray" />
          </div>
        ) : (
          <div className="space-y-1">
            <Text size="xs" className="text-doubleNickel-gray-400">
              {formatContactCount(contacts.length)} on file
            </Text>
            <PickList
              id={CONTACTS_ID}
              aria-label="Contacts on file"
              items={contacts}
              itemKey={(contact) => contact.contactId}
              renderItem={(contact) => <ContactOptionBody contact={contact} />}
              onPick={(contact, index) => onPickContact(contact, index + 1)}
              trailing={trailing}
              highlighted={keyboard.highlighted}
              onHighlight={keyboard.setHighlighted}
              onKeyDown={keyboard.onKeyDown}
            />
          </div>
        )
      ) : (
        <>
          {!loading && contacts.length === 0 && (
            <Text size="xs" className="text-doubleNickel-gray-400">
              No contacts on file
            </Text>
          )}
          <ContactRows form={form} editing />
          {contacts.length > 0 && (
            <div className="flex justify-end">
              <SectionLink onClick={onShowList}>
                {formatContactCount(contacts.length)} on file
              </SectionLink>
            </div>
          )}
        </>
      )}

      {!folded && (
        <StageActions>
          <DNButton variant="primary" onClick={onContinue}>
            Continue
          </DNButton>
        </StageActions>
      )}
    </div>
  )
}

export default ContactStage
